import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';

import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { ElementAddress, MediaText } from 'editor/src/store/design/types';
import { useDispatch } from 'editor/src/store/hooks';

import { getEcomProxyEndpoint } from 'product-personalizer/src/utils/config';

const DEBOUNCE_TIME = 500;
const FONT_RENDER_PATH = '/fonts/render';

/**
 * For custom fonts in product personalizer: request server-side SVG path generation.
 * Calls the font render API via the Shopify ecom proxy and returns the SVG path.
 * The path updates on every text/style change (debounced).
 */
export interface FontRenderResult {
  path: string;
  bbox?: { x1: number; y1: number; x2: number; y2: number };
}

function useCustomFontRender(element: MediaText, elementAddress: ElementAddress): FontRenderResult | undefined {
  const dispatch = useDispatch();
  const [renderResult, setRenderResult] = useState<FontRenderResult | undefined>(undefined);
  const lastRequestRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const elementPathRef = useRef<string | undefined>(element.path);
  elementPathRef.current = element.path;
  const elementAddressRef = useRef<ElementAddress>(elementAddress);
  elementAddressRef.current = elementAddress;

  const isCustomFont = element.extra?.fontFamily?.startsWith('custom-');

  const requestRender = useCallback(
    debounce(
      (textElement: MediaText) => {
        const fontFamily = textElement.extra?.fontFamily;
        if (!fontFamily) {
          return;
        }
        const fontId = fontFamily.startsWith('custom-') ? fontFamily.substring(7) : fontFamily;

        // Abort previous in-flight request
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const url = `${getEcomProxyEndpoint()}${FONT_RENDER_PATH}`;

        // eslint-disable-next-line no-console
        console.log('[PP:CustomFont] Requesting render', {
          font: fontFamily,
          text: textElement.extra?.text,
          url,
        });

        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            body: JSON.stringify({
              fontId,
              textElement: JSON.stringify(textElement),
            }),
          }),
          signal: controller.signal,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Font render failed: ${response.status}`);
            }
            return response.json();
          })
          .then((data: { svgPath: string; bbox?: FontRenderResult['bbox'] }) => {
            if (data.svgPath) {
              // eslint-disable-next-line no-console
              console.log('[PP:CustomFont] Render response received', {
                font: fontFamily,
                text: textElement.extra?.text,
                pathLength: data.svgPath.length,
                hasBbox: !!data.bbox,
              });
              setRenderResult({ path: data.svgPath, bbox: data.bbox });
              // Persist the server-rendered path on the design element so saveDesign exports it.
              // Without this, the design POSTed to /designs has no path and downstream preflight
              // fails with "Custom font URL not provided" (GAPI-32722).
              if (data.svgPath !== elementPathRef.current) {
                dispatch(
                  updateMediaElementOperation(
                    elementAddressRef.current,
                    { path: data.svgPath } as Partial<MediaText>,
                    MediaUpdateActionName.MOVED,
                    true,
                  ),
                );
              }
            }
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              // eslint-disable-next-line no-console
              console.warn('[PP:CustomFont] Render request failed', err);
            }
          });
      },
      DEBOUNCE_TIME,
      { leading: false, trailing: true },
    ),
    [],
  );

  useEffect(() => {
    if (!isCustomFont) {
      lastRequestRef.current = '';
      setRenderResult(undefined);
      return;
    }

    // Build cache key from visual properties (same fields as useProtectedFontRender)
    const cacheKey = [
      element.extra?.fontFamily,
      element.width,
      element.height,
      element.fill,
      element.extra?.text,
      element.extra?.fontSize,
      element.extra?.textAlign ?? '',
      element.extra?.lineHeight ?? '',
      element.extra?.underline ? '1' : '0',
      element.extra?.linethrough ? '1' : '0',
      element.extra?.direction ?? '',
      element.extra?.letterSpacing ?? '',
      element.extra?.stroke ? `${element.extra.stroke.color}${element.extra.stroke.width}` : '',
      element.extra?.shadow
        ? `${element.extra.shadow.color}${element.extra.shadow.offsetX}${element.extra.shadow.offsetY}${element.extra.shadow.blur}`
        : '',
    ].join('|');

    if (cacheKey === lastRequestRef.current) {
      return;
    }
    lastRequestRef.current = cacheKey;

    const textContent = element.extra?.text;
    if (!textContent || !textContent.trim()) {
      lastRequestRef.current = '';
      setRenderResult({ path: '' });
      if (elementPathRef.current) {
        dispatch(
          updateMediaElementOperation(
            elementAddressRef.current,
            { path: '' } as Partial<MediaText>,
            MediaUpdateActionName.MOVED,
            true,
          ),
        );
      }
      return;
    }

    requestRender(element);
  }, [
    isCustomFont,
    element.extra?.fontFamily,
    element.width,
    element.height,
    element.fill,
    element.extra?.text,
    element.extra?.fontSize,
    element.extra?.textAlign,
    element.extra?.lineHeight,
    element.extra?.underline,
    element.extra?.linethrough,
    element.extra?.direction,
    element.extra?.letterSpacing,
    element.extra?.stroke,
    element.extra?.shadow,
    element.uuid,
    requestRender,
  ]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    [],
  );

  return isCustomFont ? renderResult : undefined;
}

export default useCustomFontRender;
